import React from 'react';
import { Target, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const EVadagai = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "e-Vadagai",
      bannerAlt: "Tractor",
      
      overview: "Overview",
      overviewText1: "e-Vadagai is a digital platform developed to provide farmers with easy, transparent, and affordable access to agricultural machinery on a rental basis. Implemented through cooperative institutions, the service enables farmers—especially small and marginal farmers—to use modern farm equipment without the burden of ownership.",
      overviewText2: "By digitising the booking and allocation process, e-Vadagai ensures timely availability of machinery, standard rental charges, and improved efficiency in farm operations.",
      
      objectivesTitle: "Objectives",
      objectives: [
        "Enable farmers to access farm machinery at affordable rental rates",
        "Reduce dependency on manual labour and high-cost ownership",
        "Ensure timely agricultural operations through advance booking",
        "Promote efficient and mechanised farming practices",
        "Strengthen cooperative-led service delivery"
      ],
      
      servicesTitle: "e-Vadagai Services",
      bookNow: "Book Now",
    },
    ta: {
      title: "இ-வாடகை",
      bannerAlt: "டிராக்டர்",
      
      overview: "கண்ணோட்டம்",
      overviewText1: "இ-வாடகை என்பது விவசாயிகளுக்கு வாடகை அடிப்படையில் விவசாய இயந்திரங்களை எளிதான, வெளிப்படையான மற்றும் மலிவு விலையில் அணுகுவதற்காக உருவாக்கப்பட்ட ஒரு டிஜிட்டல் தளமாகும். கூட்டுறவு நிறுவனங்கள் மூலம் செயல்படுத்தப்படும் இந்த சேவை, குறிப்பாக சிறு மற்றும் குறு விவசாயிகள் உரிமையின் சுமை இல்லாமல் நவீன விவசாய உபகரணங்களைப் பயன்படுத்த உதவுகிறது.",
      overviewText2: "முன்பதிவு மற்றும் ஒதுக்கீடு செயல்முறையை டிஜிட்டல் மயமாக்குவதன் மூலம், இ-வாடகை இயந்திரங்களின் சரியான நேரத்தில் கிடைக்கும் தன்மை, நிலையான வாடகை கட்டணங்கள் மற்றும் விவசாய செயல்பாடுகளில் மேம்பட்ட செயல்திறனை உறுதி செய்கிறது.",
      
      objectivesTitle: "நோக்கங்கள்",
      objectives: [
        "மலிவு வாடகை விலையில் விவசாய இயந்திரங்களை விவசாயிகள் அணுக உதவுதல்",
        "கைமுறை உழைப்பு மற்றும் அதிக விலை உரிமையைச் சார்ந்திருப்பதை குறைத்தல்",
        "முன்பதிவு மூலம் சரியான நேரத்தில் விவசாய செயல்பாடுகளை உறுதி செய்தல்",
        "திறமையான மற்றும் இயந்திரமயமாக்கப்பட்ட விவசாய நடைமுறைகளை ஊக்குவித்தல்",
        "கூட்டுறவு தலைமையிலான சேவை வழங்கலை வலுப்படுத்துதல்"
      ],
      
      servicesTitle: "இ-வாடகை சேவைகள்",
      bookNow: "இப்போது முன்பதிவு செய்யுங்கள்",
    }
  };

  const t = content[language];

  const handleNavigation = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full">
  
      <div className="w-full px-6 py-12">
        
        <div className="mb-12 w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 text-center">
            {t.title}
          </h1>
        </div>

        <div className="bg-blue-50 rounded-lg p-8 mb-12 w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t.overview}</h2>
          <div className="border-l-4 border-blue-600 pl-6">
            <p className="text-gray-700 leading-relaxed">
              {t.overviewText1}
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              {t.overviewText2}
            </p>
          </div>
        </div>

        <div className="mb-12 w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t.objectivesTitle}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {t.objectives.map((obj, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-blue-200 via-blue-100 to-blue-50 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 text-white p-3 rounded-lg mt-1 flex-shrink-0">
                    <Target className="w-6 h-6" />
                  </div>
                  <p className="text-blue-900 text-base flex-1">{obj}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* E-Vadagai Services Section */}
        <div className="mb-12 w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t.servicesTitle}</h2>
          
          <div className="flex justify-center w-full">
            <div 
              onClick={() => handleNavigation('https://mts.aed.tn.gov.in/evaadagai/')}
              className="bg-gradient-to-br from-green-100 to-green-50 rounded-lg shadow-md p-8 hover:shadow-xl transition-all duration-200 cursor-pointer border border-green-200 w-full max-w-xs"
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="bg-green-500 text-white p-4 rounded-full">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-lg text-gray-800">{t.bookNow}</h3>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default EVadagai;